import { useLoaderData } from 'react-router';
import { useSetAtom } from 'jotai';

import { cn } from '@rocco/utils/cn';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';
import ProductAttributeEnum from '@modules/product/config/product-attribute';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { useVariantDecision } from '@modules/product/hook/use-variant-decision';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { purchaseModalOpenAtom } from '../state/purchase-modal-atom';

// Figma 2354:58912 — SF Pro Semibold (590); font-sans has no 600 face, so the
// real 500 cut. 15px, leading 1.22, tracking -0.05em, #767676 (matches the
// intro caption + other section labels).
const COLUMN_LABEL =
    'm-0 font-sans font-medium leading-[1.22] tracking-[-0.05em] text-[#767676] max-md:text-sm md:text-[0.9375rem]';

/**
 * F5 — Colours / Material / Size meta row between the summary row and the spec
 * accordion, per the Figma. The variant *pickers* live inside the purchase
 * drawer — clicking a swatch or chip here pre-selects that option (so the
 * drawer opens on the chosen variant) and opens the drawer. Columns self-hide
 * when they have no data; the whole section hides when all three are empty.
 * Facet options deliberately live in the spec accordion, not here.
 */
export const ProductMetaRowSection = () => {
    const { product } = useLoaderData<typeof loader>();
    const { attributeMap } = useVariantOptionState();
    const { pickOption, pickSize } = useVariantDecision(product.Variants);
    const openPurchaseModal = useSetAtom(purchaseModalOpenAtom);

    /** Pre-select the picked option, then open the purchase drawer. */
    const selectAndOpen = (attributeID: number, optionID: number) => {
        pickOption({ ID: optionID, attributeID });
        openPurchaseModal(true);
    };

    /** Sizes aren't a variant attribute — they use the dedicated size decision. */
    const selectSizeAndOpen = (sizeID: number) => {
        pickSize(sizeID);
        openPurchaseModal(true);
    };

    const colourOptions =
        attributeMap.get(ProductAttributeEnum.Colour)?.options ?? [];
    const materialOptions =
        attributeMap.get(ProductAttributeEnum.Material)?.options ?? [];
    const sizes = product.Sizes ?? [];

    if (!colourOptions.length && !materialOptions.length && !sizes.length) {
        return null;
    }

    return (
        <section className="bg-white max-md:px-6 md:px-[3rem]">
            <div
                className={cn(
                    CONTENT_CAP_PADDED,
                    'grid gap-y-6 max-md:grid-cols-1 md:grid-cols-[28.5rem_28.8rem_1fr]',
                )}
            >
                {colourOptions.length > 0 ? (
                    <div>
                        <p className={COLUMN_LABEL}>Colours</p>
                        <div className="mt-[1.5rem] flex flex-wrap gap-[0.6875rem] max-md:mt-3 md:pr-[3rem]">
                            {colourOptions.map((option) => (
                                // Styled hover tooltip with the colour name —
                                // swatches alone aren't identifiable (and the
                                // native `title` tooltip is slow to appear).
                                <TextTooltip
                                    key={option.ID}
                                    content={option.Label}
                                >
                                    <button
                                        type="button"
                                        aria-label={`Colour: ${option.Label}`}
                                        onClick={() =>
                                            selectAndOpen(
                                                ProductAttributeEnum.Colour,
                                                option.ID,
                                            )
                                        }
                                        className={cn(
                                            'size-[2.5rem] shrink-0 rounded-full border border-black/10 bg-cover bg-center',
                                            'transition-shadow hover:ring-1 hover:ring-black hover:ring-offset-2',
                                        )}
                                        style={
                                            option.Image?.Desktop
                                                ? {
                                                      backgroundImage: `url(${option.Image.Desktop})`,
                                                  }
                                                : {
                                                      backgroundColor:
                                                          option.Code ??
                                                          '#f1f1f1',
                                                  }
                                        }
                                    />
                                </TextTooltip>
                            ))}
                        </div>
                    </div>
                ) : null}

                {materialOptions.length > 0 ? (
                    <div>
                        <p className={COLUMN_LABEL}>Material</p>
                        <div className="mt-[1.5rem] flex flex-wrap gap-[0.375rem] max-md:mt-3">
                            {materialOptions.map((option) => (
                                <button
                                    key={option.ID}
                                    type="button"
                                    onClick={() =>
                                        selectAndOpen(
                                            ProductAttributeEnum.Material,
                                            option.ID,
                                        )
                                    }
                                    // Figma 2354:58916 — 30px pill, solid black
                                    // border, 15px/500 capitalised, tracking -0.05em.
                                    className="inline-flex h-[1.875rem] items-center justify-center rounded-full border border-black px-[0.875rem] font-sans font-medium tracking-[-0.05em] text-[#181818] capitalize transition-colors hover:bg-[#181818] hover:text-white max-md:text-sm md:text-[0.9375rem]"
                                >
                                    {option.Label}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}

                {sizes.length > 0 ? (
                    <div>
                        <p className={COLUMN_LABEL}>Size</p>
                        <ul className="mt-[1.5rem] flex list-none flex-col gap-[0.625rem] p-0 max-md:mt-3">
                            {sizes.map((size) => (
                                <li key={size.ID}>
                                    {/* Like the colour/material pickers, a size
                                        pre-selects that size and opens the drawer. */}
                                    <button
                                        type="button"
                                        onClick={() =>
                                            selectSizeAndOpen(size.ID)
                                        }
                                        // Figma 2354:58925 — 15px, 400, #181818 (not uppercased).
                                        className="block text-left font-sans leading-snug font-normal text-[#181818] underline-offset-2 transition-colors hover:underline max-md:text-sm md:text-[0.9375rem]"
                                    >
                                        {size.FullTitle}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}
            </div>
        </section>
    );
};
